'use client';

import { ApiKeys, fetcher } from '@insta-club/api';
import { Algoritm, Guides } from '@insta-club/models';
import { Checkbox, GuidesButton, HelpButton } from '@insta-club/ui';
import { useState } from 'react';
import toast from 'react-hot-toast';
import useSWRMutation from 'swr/mutation';

interface Props {
    algorithms: Algoritm[];
    programID: number;
    dayNumber: number;
    canPerformTask: boolean;
    onGuide: (guide: Guides) => void;
    onGuideTask: (guide: Guides) => void;
}

const AlgorithmicProgram: React.FC<Props> = ({
    algorithms,
    programID,
    dayNumber,
    canPerformTask,
    onGuide,
    onGuideTask
}) => {
    return (
        <div className='flex flex-col gap-4'>
            <div className='mb-4 flex items-center justify-between'>
                <p className='text-sm text-gray-500'>
                    {canPerformTask
                        ? 'برای ثبت فعالیتت، بعد از انجامش ✅ بزن'
                        : 'وضعیت تسک‌های برنامه تنها در همان روز یا روز بعد قابل به‌روزرسانی است.'}
                </p>
                <GuidesButton guideKey='program_algoritm' onClick={onGuide} noModal />
            </div>
            <div className='custom-scrollbar flex max-h-96 flex-col divide-y divide-gray-200 overflow-y-auto rounded-xl bg-gray-50 px-2'>
                {algorithms.map((item) => (
                    <Task
                        item={item}
                        programID={programID}
                        dayNumber={dayNumber}
                        key={`alg-${item.id}`}
                        onGuideTask={onGuideTask}
                        canPerformTask={canPerformTask}
                    />
                ))}
            </div>
        </div>
    );
};

const Task = ({
    item,
    programID,
    dayNumber,
    canPerformTask,
    onGuideTask
}: {
    item: Algoritm;
    programID: number;
    dayNumber: number;
    canPerformTask: boolean;
    onGuideTask: (guide: Guides) => void;
}) => {
    const video = item.video;
    const hasVideo = Boolean(video);
    const [loading, setLoading] = useState(false);
    const [checked, setChecked] = useState(item.is_done);

    const { trigger } = useSWRMutation(
        ApiKeys.POST_PROGRAM_TOGGLE_TASK(programID),
        (url, { arg }: { arg: { type: string; day_number: number; algoritm_id: number } }) =>
            fetcher(url, 'dashboard', 'POST', { body: arg })
    );

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setLoading(true);
        setChecked(checked);
        try {
            await trigger({
                type: 'algoritm',
                day_number: dayNumber,
                algoritm_id: item.id
            });
        } catch (err: unknown) {
            toast.removeAll();
            if (err instanceof Error) {
                toast.error('خطای ناشناخته رخ داده است');
            } else {
                const error = err as { status: number; message: string };
                if (error.status && error.status == 400) {
                    toast.error('وضعیت تسک های برنامه فقط در همان روز یا روز بعد قابل بروز رسانی می باشند.');
                }
            }
            setChecked(!checked);
        } finally {
            setLoading(false);
        }
    };

    const handleTaskGuide = () => {
        onGuideTask({
            location: `guide-${item.id}`,
            video: {
                hls: video.hls,
                url: video.url
            }
        });
    };

    return (
        <>
            <div className='flex items-center gap-2 p-4'>
                <div className='flex flex-1 items-center'>
                    {canPerformTask ? (
                        <Checkbox
                            checked={checked}
                            onChange={handleChange}
                            disabled={loading}
                            label={
                                <span className='relative top-0.5 text-sm leading-relaxed font-semibold text-gray-900'>
                                    {item.text}
                                </span>
                            }
                        />
                    ) : (
                        <span className='text-sm leading-relaxed font-semibold text-gray-900'>{item.text}</span>
                    )}
                </div>
                {hasVideo && <HelpButton onClick={handleTaskGuide} title={''} />}
            </div>
        </>
    );
};

export default AlgorithmicProgram;

